/* Sparkline — tiny inline SVG bar/line chart for compact data viz.
 *
 * Used in the Compliance Insights table to show 30-day wear-time per
 * patient. Sized to sit inline with the 7d avg pill in the table cell.
 *
 * Renders bars (not a line) because wear-time is discrete daily data
 * with frequent zeros (missed days). Bars convey "missed days" visually
 * better than a line that dips to zero and back up.
 */
import React from "react";


export default function Sparkline({
    data = [],
    width = 90,
    height = 28,
    barColor = "currentColor",
    zeroColor = "#e5e7eb",       // muted grey for missed-day stubs
    targetLine = null,           // optional horizontal reference (e.g. 22h target)
    targetColor = "rgba(0,0,0,0.15)",
}) {
    if (!data || data.length === 0) {
        return (
            <div
                className="inline-block"
                style={{ width, height, opacity: 0.35 }}
                data-testid="sparkline-empty"
            >
                <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                    <line x1="0" y1={height / 2} x2={width} y2={height / 2}
                          stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />
                </svg>
            </div>
        );
    }
    const n = data.length;
    const maxVal = Math.max(24, ...data);     // 24h ceiling — keeps the scale consistent
    const gap = 1;
    const barWidth = (width - gap * (n - 1)) / n;
    const targetY = targetLine != null ? height - (targetLine / maxVal) * height : null;

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="inline-block align-middle"
            data-testid="sparkline"
        >
            {targetY != null && (
                <line
                    x1="0" y1={targetY} x2={width} y2={targetY}
                    stroke={targetColor} strokeWidth="1" strokeDasharray="2 2"
                />
            )}
            {data.map((v, i) => {
                const h = v > 0 ? Math.max(2, (v / maxVal) * height) : 2;
                const x = i * (barWidth + gap);
                const y = height - h;
                return (
                    <rect
                        key={i}
                        x={x} y={y}
                        width={barWidth} height={h}
                        fill={v > 0 ? barColor : zeroColor}
                        rx="0.5"
                    >
                        <title>{v > 0 ? `${v.toFixed(1)}h` : "No log"}</title>
                    </rect>
                );
            })}
        </svg>
    );
}
